import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import yaml from 'js-yaml';
import {
  Card, CardContent, Box, Typography, Chip,
  ButtonGroup, Button, List, ListItem, Stack
} from '@mui/material';
import { keyframes } from '@emotion/react';

const axolotlBob = keyframes`
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50% { transform: translateY(-6px) rotate(4deg); }
`;

const axolotlTailWag = keyframes`
  0%, 100% { transform: rotate(-12deg); }
  50% { transform: rotate(12deg); }
`;

const Axolotl = ({ size = 32, uniform = '' }) => {
  const isFormal = uniform.toLowerCase().includes('formal');
  const isSporty = uniform.toLowerCase().includes('sport') || uniform.toLowerCase().includes('house');

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        animation: `${axolotlBob} 2.2s ease-in-out infinite`,
        lineHeight: 0,
      }}
    >
      <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
        {/* tail */}
        <path
          d="M10 40 C2 38, 2 50, 12 48 C16 47, 18 43, 18 40 Z"
          fill="#f4a7c6"
          style={{ transformOrigin: '16px 44px', animation: `${axolotlTailWag} 1s ease-in-out infinite` }}
        />
        {/* body */}
        <ellipse cx="34" cy="40" rx="20" ry="14" fill="#f9c4dd" />
        {/* head */}
        <circle cx="44" cy="28" r="16" fill="#fbd0e4" />
        {/* gills left */}
        <g stroke="#e8799f" strokeWidth="2" strokeLinecap="round">
          <path d="M30 18 C24 16, 20 12, 22 8" />
          <path d="M30 22 C23 21, 18 19, 18 14" />
          <path d="M31 27 C24 27, 18 27, 16 23" />
        </g>
        {/* gills right */}
        <g stroke="#e8799f" strokeWidth="2" strokeLinecap="round">
          <path d="M50 14 C52 9, 50 5, 45 4" />
          <path d="M54 18 C57 14, 57 9, 52 7" />
          <path d="M56 23 C60 21, 61 16, 57 13" />
        </g>
        {/* feet */}
        <ellipse cx="22" cy="48" rx="5" ry="3" fill="#f9c4dd" />
        <ellipse cx="46" cy="50" rx="5" ry="3" fill="#f9c4dd" />

        {/* sports/house shorts, drawn before face so it sits on the body */}
        {isSporty && (
          <g>
            <path d="M18 42 Q34 50 50 42 L50 47 Q34 54 18 47 Z" fill="#3a6ea5" />
            <line x1="34" y1="44" x2="34" y2="51" stroke="#2c5680" strokeWidth="1.4" />
          </g>
        )}

        {/* eyes */}
        <circle cx="39" cy="25" r="2.2" fill="#3a2230" />
        <circle cx="50" cy="25" r="2.2" fill="#3a2230" />
        {/* smile */}
        <path d="M41 33 Q44.5 36 48 33" stroke="#3a2230" strokeWidth="1.6" fill="none" strokeLinecap="round" />

        {/* formal hat */}
        {isFormal && (
          <g>
            <ellipse cx="44" cy="15" rx="11" ry="3" fill="#2c3e6b" />
            <path d="M35 15 Q44 1 53 15 Z" fill="#3a4f8a" />
            <ellipse cx="44" cy="15" rx="2" ry="1.4" fill="#d4af37" />
          </g>
        )}
      </svg>
    </Box>
  );
};

const MS_PER_DAY = 1000 * 3600 * 24;

// 'YYYY-MM-DD' is parsed as local midnight, not UTC. new Date('2026-07-20')
// yields UTC midnight, which lands on the 19th anywhere behind UTC and would
// shift the Week A/B anchor by a whole week for those viewers.
const atMidnight = (value) => {
  if (typeof value === 'string') {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

// Monday of the week containing `date`. Week A/B and week numbers are both
// whole-week concepts, so every comparison is done between Mondays.
const mondayOf = (date) => {
  const monday = atMidnight(date);
  const dayOfWeek = monday.getDay(); // 0 = Sunday
  monday.setDate(monday.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  return monday;
};

// 2026 term dates
const TERMS = [
  { number: 1, start: '2026-02-02', end: '2026-04-02' },
  { number: 2, start: '2026-04-20', end: '2026-06-26' },
  { number: 3, start: '2026-07-21', end: '2026-09-25' },
  { number: 4, start: '2026-10-12', end: '2026-12-08' },
].map(term => ({ ...term, start: atMidnight(term.start), end: atMidnight(term.end) }));

// Known-good reference point: the week beginning Monday 20 July 2026 is Week B.
// A/B alternation runs continuously and is NOT interrupted by the holidays, so
// this single anchor fixes the letter for every week of the year.
const WEEK_TYPE_ANCHOR = mondayOf('2026-07-20');
const WEEK_TYPE_AT_ANCHOR = 'weekB';

const getWeekType = () => {
  const now = atMidnight(new Date());
  const thisMonday = mondayOf(now);

  // Week A/B: continuous alternation from the anchor, in both directions.
  const weeksFromAnchor = Math.round((thisMonday - WEEK_TYPE_ANCHOR) / (MS_PER_DAY * 7));
  const matchesAnchor = Math.abs(weeksFromAnchor % 2) === 0;
  const weekType = matchesAnchor
    ? WEEK_TYPE_AT_ANCHOR
    : (WEEK_TYPE_AT_ANCHOR === 'weekA' ? 'weekB' : 'weekA');

  // Term: the term we're currently in, or during a break the term coming up.
  // The last term is kept once the year has finished.
  const currentTerm = TERMS.find(term => now <= term.end) ?? TERMS[TERMS.length - 1];

  // Week number restarts at 1 each term. A break week counts as week 1 of the
  // term it leads into, so the final holiday Monday already reads "Week 1".
  const weeksIntoTerm = Math.round((thisMonday - mondayOf(currentTerm.start)) / (MS_PER_DAY * 7));
  const weekNumber = Math.max(1, weeksIntoTerm + 1);

  return { weekType, weekNumber, termNumber: currentTerm.number };
};

const isInSchoolTerm = () => {
  const now = atMidnight(new Date());
  return TERMS.some(term => now >= term.start && now <= term.end);
};

const getSchoolStatus = () => {
  const now = atMidnight(new Date());

  const term1Start = TERMS[0].start;
  const term4End = TERMS[TERMS.length - 1].end;

  if (now < term1Start) {
    const daysUntilStart = Math.ceil((term1Start - now) / (1000 * 3600 * 24));
    return {
      status: 'before-term',
      message: `School starts in ${daysUntilStart} day${daysUntilStart === 1 ? '' : 's'}`,
      subtitle: `Term 1 begins on ${term1Start.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
    };
  } else if (now > term4End) {
    return {
      status: 'after-term',
      message: 'School year has ended',
      subtitle: `Term 4 ended on ${term4End.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
    };
  } else {
    return {
      status: 'holiday',
      message: 'School holidays',
      subtitle: 'Enjoy your break!'
    };
  }
};

const STATUS_CHIP = {
  'before-term': { label: 'Preview Mode', color: 'info' },
  'after-term': { label: 'Archive Mode', color: 'success' },
  'holiday': { label: 'Holiday Mode', color: 'warning' },
};

const Schedule = () => {
  const [schedule, setSchedule] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [daySchedule, setDaySchedule] = useState([]);
  const [dayUniform, setDayUniform] = useState('');
  const [uniformImage, setUniformImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentWeekType, setCurrentWeekType] = useState('');
  const [displayedWeekType, setDisplayedWeekType] = useState('');
  const [displayedWeekNumber, setDisplayedWeekNumber] = useState(0);
  const [displayedTermNumber, setDisplayedTermNumber] = useState(3);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Use useMemo to memoize today's value
  const today = useMemo(() => {
    return new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  }, []);

  const currentWeek = getWeekType(); // 2026 term dates are now built into the function
  const inSchoolTerm = isInSchoolTerm();
  const schoolStatus = getSchoolStatus();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch schedule data
        const scheduleResponse = await axios.get('/api/get-schedule').catch(() => axios.get('/data/schedule.yml'));
        const scheduleData = yaml.load(scheduleResponse.data);
        setSchedule(scheduleData);

        const { weekType, weekNumber, termNumber } = getWeekType(); // 2026 term dates are built into the function
        setCurrentWeekType(weekType);
        setDisplayedWeekType(weekType);
        setDisplayedWeekNumber(weekNumber);
        setDisplayedTermNumber(termNumber);
        setSelectedDay(today);

        const todayData = scheduleData[weekType][today];
        setDaySchedule(todayData?.schedule || []);
        setDayUniform(todayData?.uniform || '');
        setUniformImage(todayData?.uniformImage || '');

        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [today]); // Add today to the dependency array

  const handleDayChange = (day) => {
    setSelectedDay(day);
    const dayData = schedule[currentWeekType][day];
    setDaySchedule(dayData?.schedule || []);
    setDayUniform(dayData?.uniform || '');
    setUniformImage(dayData?.uniformImage || '');
  };

  const handleWeekChange = (weekType) => {
    // Update the current week type when a button is clicked
    setCurrentWeekType(weekType);
    // Do not change displayedWeekType or displayedWeekNumber
    const dayData = schedule[weekType][selectedDay];
    setDaySchedule(dayData?.schedule || []);
    setDayUniform(dayData?.uniform || '');
    setUniformImage(dayData?.uniformImage || '');
  };

  const handleTodayClick = () => {
    setSelectedDay(today);
    setCurrentWeekType(currentWeek.weekType);
    const todayData = schedule[currentWeek.weekType][today];
    setDaySchedule(todayData?.schedule || []);
    setDayUniform(todayData?.uniform || '');
    setUniformImage(todayData?.uniformImage || '');
  };

  if (loading) {
    return <Box sx={{ p: 4 }}>Loading schedule...</Box>;
  }

  const dayLabel = (day) => day.slice(0, 3).charAt(0).toUpperCase() + day.slice(1, 3);
  const isWeekend = selectedDay === 'saturday' || selectedDay === 'sunday';

  const scheduleList = (
    <List disablePadding sx={{ borderTop: 1, borderColor: 'divider' }}>
      {isWeekend ? (
        <ListItem divider>No activities scheduled for weekends</ListItem>
      ) : daySchedule.length > 0 ? (
        daySchedule.map((item, index) => (
          <ListItem key={index} divider sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight="bold">{item.time}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>{item.activity}</Typography>
              {item.image && (
                <Box component="img" src={item.image} alt={item.activity} sx={{ height: 30, borderRadius: 1 }} />
              )}
            </Stack>
          </ListItem>
        ))
      ) : (
        <ListItem divider>No activities scheduled for this day</ListItem>
      )}
    </List>
  );

  const weekButtonGroup = (
    <ButtonGroup size="small">
      <Button
        variant={currentWeekType === 'weekA' ? 'contained' : 'outlined'}
        onClick={() => handleWeekChange('weekA')}
      >
        Week A
      </Button>
      <Button
        variant={currentWeekType === 'weekB' ? 'contained' : 'outlined'}
        onClick={() => handleWeekChange('weekB')}
      >
        Week B
      </Button>
    </ButtonGroup>
  );

  const dayButtonGroup = (
    <ButtonGroup size="small" fullWidth sx={{ mb: 2 }}>
      {days.map((day) => {
        const isToday = day === today && currentWeekType === currentWeek.weekType;
        return (
          <Button
            key={day}
            variant={selectedDay === day ? 'contained' : 'outlined'}
            onClick={() => handleDayChange(day)}
            sx={isToday ? { borderColor: 'success.main', boxShadow: '0 0 0 2px rgba(46,110,52,0.25)' } : undefined}
          >
            {dayLabel(day)}{isToday && ' •'}
          </Button>
        );
      })}
    </ButtonGroup>
  );

  // Show different content when school is not in session
  if (!inSchoolTerm) {
    const chip = STATUS_CHIP[schoolStatus.status];
    return (
      <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} mb={2}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5">1C Axolotl Schedule</Typography>
                <Axolotl uniform={dayUniform} />
              </Stack>
              <Typography variant="subtitle1" color="text.secondary">{schoolStatus.message}</Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>{schoolStatus.subtitle}</Typography>
              {chip && <Chip label={chip.label} color={chip.color} size="small" />}
            </Box>
            <Box mt={{ xs: 2, md: 0 }}>{weekButtonGroup}</Box>
          </Stack>

          {dayButtonGroup}

          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip label={`Uniform: ${dayUniform}`} color="info" size="small" />
            {uniformImage && (
              <Box component="img" src={uniformImage} alt={dayUniform} sx={{ height: 80, borderRadius: 1 }} />
            )}
          </Stack>
        </CardContent>
        {scheduleList}
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} mb={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5">1C Axolotl Schedule</Typography>
              <Axolotl uniform={dayUniform} />
            </Stack>
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              Today is {today.charAt(0).toUpperCase() + today.slice(1)}, {displayedWeekType === 'weekA' ? 'Week A' : 'Week B'} (Week {displayedWeekNumber}, Term {displayedTermNumber})
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" mt={{ xs: 2, md: 0 }}>
            <Button variant="contained" color="success" size="small" onClick={handleTodayClick}>
              Today
            </Button>
            {weekButtonGroup}
          </Stack>
        </Stack>

        {dayButtonGroup}

        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip label={`Uniform: ${dayUniform}`} color="info" size="small" />
          {uniformImage && (
            <Box component="img" src={uniformImage} alt={dayUniform} sx={{ height: 80, borderRadius: 1 }} />
          )}
        </Stack>
      </CardContent>
      {scheduleList}
    </Card>
  );
};

export default Schedule;
